import { useEffect, useMemo, useState } from 'react';
import { COUNTRIES } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import {
  useRegistrationCountrySettings,
  useUpdateRegistrationCountrySettings,
} from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Allow-list paesi in registrazione (super-admin only). Caso reale 26/08:
 * un attore genera domini email freschi più veloce di quanto un blocklist
 * possa inseguirlo, ma i suoi numeri sono sempre di paesi diversi — un
 * numero reale per paese costa, un dominio email gratis no.
 */
export function RegistrationCountrySettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useRegistrationCountrySettings(true);
  const updateMutation = useUpdateRegistrationCountrySettings();

  const [enabled, setEnabled] = useState(false);
  const [allowedDials, setAllowedDials] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled ?? false);
      setAllowedDials(settings.allowedDials ?? []);
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [search]);

  function toggleDial(dial: string) {
    setAllowedDials((prev) => (prev.includes(dial) ? prev.filter((d) => d !== dial) : [...prev, dial]));
  }

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({ enabled, allowedDials });
      setToast({ type: 'success', message: 'Impostazioni paesi ammessi salvate con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio: ${extractErrorDetail(err)}` });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9 9 0 100-18 9 9 0 000 18ZM3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 000 18M12.5 3a17 17 0 010 18" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Paesi ammessi in registrazione</h3>
            <p className="text-xs text-neutral-600">Accetta solo numeri di telefono dei paesi selezionati (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <p className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-600">
          Se attivo, la registrazione rifiuta qualunque numero il cui prefisso
          non sia tra quelli spuntati sotto (es. solo Italia e Svizzera). Non
          tocca gli utenti già registrati, solo le nuove registrazioni.
        </p>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-500">
              {allowedDials.length} paes{allowedDials.length === 1 ? 'e' : 'i'} selezionat{allowedDials.length === 1 ? 'o' : 'i'}
            </span>
            {allowedDials.length > 0 && (
              <button
                type="button"
                onClick={() => setAllowedDials([])}
                className="text-xs text-neutral-400 hover:text-neutral-600"
              >
                Svuota selezione
              </button>
            )}
          </div>

          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca paese o prefisso (es. Italia, +39)…"
          />

          <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-neutral-200">
            {filteredCountries.map((c) => (
              <label
                key={c.code}
                className="flex cursor-pointer items-center gap-2.5 border-b border-neutral-100 px-3 py-2 text-sm last:border-b-0 hover:bg-neutral-50"
              >
                <input
                  type="checkbox"
                  checked={allowedDials.includes(c.dial)}
                  onChange={() => toggleDial(c.dial)}
                  className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                />
                <span>{c.flag}</span>
                <span className="text-neutral-700">{c.name}</span>
                <span className="ml-auto font-mono text-xs text-neutral-400">{c.dial}</span>
              </label>
            ))}
            {filteredCountries.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-neutral-400">Nessun paese trovato.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva paesi ammessi
          </Button>
        </div>
      </div>
    </div>
  );
}
